import { useId, useMemo, useState, type KeyboardEvent } from "react";
import { ListChecks, PackageOpen, Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { OrderItem } from "@/lib/api";
import { cn } from "@/lib/utils";
import { OrderItemCard } from "./OrderItemCard";
import {
  groupItemsBySeries,
  matchesItemSearch,
  type ItemRequestVariant,
  type OrderItemGroup,
  type SelectableOrderItem,
} from "./supportRequest";

const SELECTION_ERROR_ID = "order-item-selector-error";
/** A busca só aparece em pedidos maiores: com poucos itens, ela seria ruído. */
const SEARCH_MIN_ITEMS = 6;
/** Até esse número de séries, todas começam abertas. */
const MAX_GROUPS_OPEN_BY_DEFAULT = 3;

type Group = OrderItemGroup<SelectableOrderItem>;

/** Abertas ao montar: todas (pedido com poucas séries) ou a primeira, mais as com item marcado (ao voltar ao passo). */
function getInitialOpenGroups(groups: Group[], selectedIds: string[]): string[] {
  return groups
    .filter(
      (group, index) =>
        groups.length <= MAX_GROUPS_OPEN_BY_DEFAULT ||
        index === 0 ||
        group.items.some((item) => selectedIds.includes(item.id))
    )
    .map((group) => group.key);
}

/** Busca e, com "Só selecionados" ligado, só os itens de `onlyIds`; séries sem nenhum item somem. */
function filterGroups(groups: Group[], query: string, onlyIds: string[] | null = null): Group[] {
  if (!query.trim() && !onlyIds) return groups;
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => matchesItemSearch(item, query) && (!onlyIds || onlyIds.includes(item.id))),
    }))
    .filter((group) => group.items.length > 0);
}

interface OrderItemSelectorProps {
  items: SelectableOrderItem[];
  variant: ItemRequestVariant;
  /** Itens marcados — vão todos na mesma solicitação (um chamado só). */
  selectedIds: string[];
  onToggle: (itemId: string, selected: boolean) => void;
  /** Quantidade de cada item marcado (troca: quantos trocar; conferência: recebida), editada no card dele. */
  quantities: Record<string, string>;
  onQuantityChange: (itemId: string, quantity: string) => void;
  getItemUsage?: (item: OrderItem) => string | null | undefined;
  /** Erro da seleção como um todo (ex.: nenhum item marcado). */
  error?: string;
  /** Erro de cada item (quantidade inválida, recusa da API), pelo id do item. */
  itemErrors?: Record<string, string>;
  disabled?: boolean;
  /** Id do rótulo que descreve o grupo (título do passo). */
  labelledBy?: string;
}

/**
 * Seletor dos itens do pedido para solicitações por item, agrupados por série num Accordion, com busca por nome ou
 * SKU em pedidos maiores. Vários itens podem ser marcados; cada um marcado ganha o campo de quantidade no card.
 */
export const OrderItemSelector = ({
  items,
  variant,
  selectedIds,
  onToggle,
  quantities,
  onQuantityChange,
  getItemUsage,
  error,
  itemErrors = {},
  disabled,
  labelledBy,
}: OrderItemSelectorProps) => {
  const searchId = useId();
  const groups = useMemo(() => groupItemsBySeries(items), [items]);
  const [query, setQuery] = useState("");
  const [openGroups, setOpenGroups] = useState(() => getInitialOpenGroups(groups, selectedIds));
  // "Só selecionados": os marcados no momento em que o filtro foi ligado. Desmarcar um deles não o tira da tela
  // (a linha não some debaixo do cursor); a lista só muda quando o filtro é desligado.
  const [pinnedIds, setPinnedIds] = useState<string[] | null>(null);
  const visibleGroups = filterGroups(groups, query, pinnedIds);
  // Série com item recusado fica aberta até o erro sair, pra ele nunca ficar escondido.
  const groupsWithErrors = groups
    .filter((group) => group.items.some((item) => itemErrors[item.id]))
    .map((group) => group.key);
  const expandedGroups = Array.from(new Set([...openGroups, ...groupsWithErrors]));

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-[10px] border border-dashed px-6 py-8 text-center">
        <PackageOpen className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Este pedido não tem itens disponíveis para solicitação.</p>
      </div>
    );
  }

  // Buscar abre as séries com resultado, sem impedir que o cliente feche alguma depois.
  const handleSearchChange = (value: string) => {
    setQuery(value);
    if (value.trim()) setOpenGroups(filterGroups(groups, value, pinnedIds).map((group) => group.key));
  };

  // Ligar também abre as séries com resultado; desligar mantém as séries como estão.
  const handleToggleOnlySelected = () => {
    if (pinnedIds) {
      setPinnedIds(null);
      return;
    }
    setPinnedIds(selectedIds);
    setOpenGroups(filterGroups(groups, query, selectedIds).map((group) => group.key));
  };

  // Enter na busca não pode avançar o passo (submit implícito do formulário).
  const preventSubmit = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") event.preventDefault();
  };

  return (
    <div className="flex flex-col gap-3">
      {items.length >= SEARCH_MIN_ITEMS && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <label htmlFor={searchId} className="sr-only">
              Buscar produto por nome ou SKU
            </label>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id={searchId}
              type="search"
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={preventSubmit}
              placeholder="Buscar produto ou SKU"
              disabled={disabled}
              autoComplete="off"
              className="h-10 pl-9 text-[14.5px]"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleToggleOnlySelected}
            disabled={disabled || (!pinnedIds && selectedIds.length === 0)}
            aria-pressed={!!pinnedIds}
            className={cn(
              "h-10 shrink-0 gap-2 text-[14px]",
              pinnedIds && "border-primary bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
            )}
          >
            <ListChecks className="h-4 w-4" aria-hidden="true" />
            Só selecionados ({selectedIds.length})
          </Button>
        </div>
      )}

      <div role="group" aria-labelledby={labelledBy} aria-describedby={error ? SELECTION_ERROR_ID : undefined}>
        {visibleGroups.length === 0 ? (
          <p className="rounded-[10px] border border-dashed px-6 py-6 text-center text-sm text-muted-foreground">
            {query.trim()
              ? `Nenhum produto encontrado para "${query.trim()}".`
              : "Nenhum produto selecionado."}
          </p>
        ) : (
          <Accordion type="multiple" value={expandedGroups} onValueChange={setOpenGroups} className="flex flex-col gap-2">
            {visibleGroups.map((group) => {
              const selectedCount = group.items.filter((item) => selectedIds.includes(item.id)).length;
              return (
                <AccordionItem key={group.key} value={group.key} className="rounded-[10px] border bg-background">
                  <AccordionTrigger className="gap-3 px-4 py-3 text-left hover:no-underline">
                    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                      <span className="text-[15px] font-bold">{group.key}</span>
                      {group.segment && <span className="text-[13px] text-muted-foreground">{group.segment}</span>}
                    </span>
                    <span
                      className={
                        selectedCount > 0
                          ? "shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
                          : "shrink-0 text-xs text-muted-foreground"
                      }
                    >
                      {selectedCount > 0
                        ? `${selectedCount} ${selectedCount === 1 ? "selecionado" : "selecionados"}`
                        : `${group.items.length} ${group.items.length === 1 ? "produto" : "produtos"}`}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-2.5 px-3 pb-3">
                    {group.items.map((item) => (
                      <OrderItemCard
                        key={item.id}
                        item={item}
                        variant={variant}
                        selected={selectedIds.includes(item.id)}
                        onToggle={onToggle}
                        quantity={quantities[item.id] ?? ""}
                        onQuantityChange={onQuantityChange}
                        usage={getItemUsage?.(item)}
                        error={itemErrors[item.id]}
                        disabled={disabled}
                      />
                    ))}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      {error && (
        <p id={SELECTION_ERROR_ID} role="alert" className="text-[13px] font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};
